import PageFooter from '@components/PageFooter/PageFooter';
import PageHeader from '@components/PageHeader/PageHeader';
import Steps from '@components/Steps/Steps';
import ThankYouPage from '@components/ThankYouPage/ThankYouPage';
import { Loader } from '@gemini/lux-ds-pms';
import useGetGeneralPracticeInfo from '@hooks/useGetGeneralPracticeInfo';
import { Modes } from '@utils/enums/Modes.enum';
import { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { useOnlineBookingStore } from './store/onlineBookingStore';
import useGetFormConfiguration from '@hooks/useGetFormConfiguration';
import './App.scss';
import useGetGeneralConfig from '@hooks/useGetGeneralConfig';
import { buildExternalLinks } from '@utils/functions';
import CancelConfirmPage from '@components/CancelConfirmPage/CancelConfirmPage';
import RescheduleFlow from '@components/RescheduleFlow/RescheduleFlow';
import { LuxTypography, Stack } from '@gemini/fe-ds-core';
import { EventProvider } from '@gemini/fe-utils';
import useSchedulingConfig from '@hooks/useSchedulingConfig';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import { FlowModes } from '@utils/enums/FlowModes.enum';
import { PracticeCardPage } from '@components/PracticeCardPage/PracticeCardPage';
import ErrorManagementComponent from '@components/ErrorManagementComponent/ErrorManagementComponent';

export interface AppProps {
  mode: Modes;
  hideHeader?: boolean;
  collapsableMobileBookingDetails?: boolean;
}

/**
 * Get configuration and information of practice
 */
function App({
  mode,
  hideHeader = false,
  collapsableMobileBookingDetails = false,
}: AppProps) {
  const { t } = useTranslation();
  const location = useLocation();
  console.log('@location', location);
  const { Urls } = useUrlsContext();
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const flowMode = useOnlineBookingStore(state => state.flowMode);
  const setGeneralInfo = useOnlineBookingStore(state => state.setGeneralInfo);
  const setScheduleConfig = useOnlineBookingStore(
    state => state.setScheduleConfig,
  );
  const setGeneralConfig = useOnlineBookingStore(
    state => state.setGeneralConfig,
  );
  const setFormConfigs = useOnlineBookingStore(state => state.setFormConfigs);

  const setFormStep = useOnlineBookingStore(state => state.setFormStep);
  const {
    data: practiceInfo,
    isLoading: isPracticeInfoLoading,
    error: practiceInfoError,
  } = useGetGeneralPracticeInfo({
    practiceId: practiceId,
  });

  const brandName = useOnlineBookingStore(state => state.brandName);

  const {
    data: formData,
    isLoading: isFormDataLoading,
    error: formDataError,
  } = useGetFormConfiguration({
    practiceId: practiceId,
    isBranded: !!brandName,
  });
  const {
    data: config,
    isLoading: isConfigLoading,
    error: configError,
  } = useGetGeneralConfig({
    practiceId: practiceId,
    mode: mode,
  });
  const {
    data: scheduleConfig,
    isLoading: isScheduleConfigLoading,
    error: scheduleConfigError,
  } = useSchedulingConfig({
    practiceId: practiceId,
  });

  useEffect(() => {
    if (practiceInfo) {
      setGeneralInfo(practiceInfo);
    }
  }, [practiceInfo]);

  useEffect(() => {
    if (formData) {
      setFormConfigs(formData);
    }
  }, [formData]);

  useEffect(() => {
    if (config) {
      setGeneralConfig(config);
    }
  }, [config]);

  useEffect(() => {
    if (scheduleConfig) {
      setScheduleConfig(scheduleConfig);
    }
  }, [scheduleConfig]);

  const formConfigs = useOnlineBookingStore(state => state.formConfigs);

  const showPatientInsurance = useMemo(() => {
    return formConfigs?.showPatientInsurance;
  }, [formConfigs]);

  const stepMapping = useMemo((): Record<string, number> => {
    return {
      [Urls.APPOINTMENT_TYPE]: 0,
      [Urls.INSURANCE]: 1,
      [Urls.SCHEDULE_EXAM]: 2,
      [Urls.PATIENT_DETAILS]: 3,
      [Urls.CUSTOM_PAGE]: 4,
      [Urls.CONTACT_DETAILS]: 5,
    };
  }, [showPatientInsurance]);

  // this effect is used to avoid automatic zoom on input focus on mobile iOS devices
  // it is a meta tag which defines viewport properties
  // added also meta to avoid automatic hyperlinking of phone numbers, dates, addresses and emails
  useEffect(() => {
    const metaViewport = document.createElement('meta');
    metaViewport.name = 'viewport';
    metaViewport.content =
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
    document.head.appendChild(metaViewport);

    const metaFormatDetection = document.createElement('meta');
    metaFormatDetection.name = 'format-detection';
    metaFormatDetection.content = 'telephone=no, date=no, address=no, email=no';
    document.head.appendChild(metaFormatDetection);

    return () => {
      document.head.removeChild(metaViewport);
      document.head.removeChild(metaFormatDetection);
    };
  }, []);

  useEffect(() => {
    const stepNumber = stepMapping[location.pathname] ?? 0;
    console.log('#App.stepNumber: ', stepNumber);
    setFormStep(stepNumber);
  }, [location, setFormStep, stepMapping]);

  if (!practiceId) {
    // * Stack needed to fix accessibility issue
    // * due to html and body tags CSS configuration

    return (
      <Stack
        alignItems={'center'}
        justifyContent={'center'}
        tabIndex={0}
        height={'100vh'}
        overflow={'auto'}
      >
        <LuxTypography>
          Practice ID is required in url. Ex:
          /steps/appointment-type?practiceId=....
        </LuxTypography>
      </Stack>
    ); // Messaggio di errore
  }

  return (
    <EventProvider global>
      {isFormDataLoading ||
      isPracticeInfoLoading ||
      isConfigLoading ||
      isScheduleConfigLoading ? (
        <Loader />
      ) : practiceInfoError ||
        formDataError ||
        configError ||
        scheduleConfigError ? (
        <ErrorManagementComponent
          practiceInfoError={practiceInfoError}
          formDataError={formDataError}
        />
      ) : (
        practiceInfo &&
        formData &&
        config &&
        scheduleConfig && (
          <div className="OnlineBookingWrapper">
            <Helmet>
              <title>{t('wcag.ob_general_title')}</title>
            </Helmet>
            <div
              style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {!hideHeader && (
                <PageHeader
                  showLogo={config.customization?.showLogo}
                  logo={practiceInfo.logo}
                  practiceName={practiceInfo.name}
                  mode={mode}
                />
              )}
              <Switch>
                <Route path={Urls.STEPS}>
                  <Steps
                    hideHeader={hideHeader}
                    collapsableMobileBookingDetails={
                      collapsableMobileBookingDetails
                    }
                  />
                </Route>
                <Route path={[Urls.TYP, Urls.RESCHEDULE_CONFIRMED]}>
                  <ThankYouPage config={config} />
                </Route>
                <Route path={Urls.MANAGE}>
                  <CancelConfirmPage />
                </Route>
                <Route path={Urls.RESCHEDULE}>
                  <RescheduleFlow />
                </Route>
                <Route path={Urls.PRACTICE_CARD}>
                  <PracticeCardPage />
                </Route>
                <Route>
                  <Redirect
                    to={
                      flowMode === FlowModes.RESCHEDULE
                        ? Urls.RESCHEDULE
                        : flowMode === FlowModes.CANCEL ||
                            flowMode === FlowModes.CONFIRM
                          ? `${Urls.MANAGE}?mode=${flowMode}`
                          : Urls.STEPS
                    }
                  />
                </Route>
              </Switch>
            </div>
            {buildExternalLinks(config)?.length > 0 && (
              <PageFooter externalLinks={buildExternalLinks(config)} />
            )}
          </div>
        )
      )}
    </EventProvider>
  );
}

export default App;
